import { useEffect, useRef, useCallback } from 'react'

interface UseUnsavedChangesOptions {
  hasChanges: boolean
  message?: string
}

export const useUnsavedChanges = ({
  hasChanges,
  message = 'You have unsaved changes. Are you sure you want to leave?'
}: UseUnsavedChangesOptions) => {
  const isNavigatingRef = useRef(false)

  // Handle browser navigation (back button, refresh, close tab)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges && !isNavigatingRef.current) {
        e.preventDefault()
        e.returnValue = message
        return message
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasChanges, message])

  // Handle React Router navigation
  useEffect(() => {
    if (!hasChanges) return

    const handleNavigation = () => {
      if (hasChanges && !isNavigatingRef.current) {
        const confirmLeave = window.confirm(message)
        if (!confirmLeave) {
          // Prevent navigation by pushing current state back
          window.history.pushState(null, '', window.location.href)
        } else {
          isNavigatingRef.current = true
        }
      }
    }

    window.addEventListener('popstate', handleNavigation)
    return () => window.removeEventListener('popstate', handleNavigation)
  }, [hasChanges, message])

  const confirmNavigation = useCallback((navigateTo: () => void) => {
    if (hasChanges) {
      const confirmLeave = window.confirm(message)
      if (confirmLeave) {
        isNavigatingRef.current = true
        navigateTo()
      }
    } else {
      navigateTo()
    }
  }, [hasChanges, message])

  return {
    confirmNavigation,
    setNavigating: (value: boolean) => {
      isNavigatingRef.current = value
    }
  }
}